"use client";
import { Capacitor } from "@capacitor/core";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Switch } from "@/components/ui/switch";
import { LEGAL_LINKS } from "@/constants/legal";
import { useAuthState } from "@/hooks/use-auth-state";
import {
  ACQUISITION_CHANGED,
  observeAcquisitionUrl,
  readClientAcquisition,
  resetAcquisitionAccountBoundary,
  saveClientAcquisition,
  setAdMeasurementConsent,
  setupAcquisitionStorageSync,
  setupStoreClickTracking,
  syncAcquisition,
} from "@/lib/acquisition/client";
import {
  requestNativeTracking,
  setupNativeAcquisition,
  setupNativeAcquisitionLifecycle,
  updateNativeAdvertising,
} from "@/lib/acquisition/native";
import { measurementOn } from "@/lib/acquisition/shared";
import {
  setWebAdvertisingUser,
  updateWebAdvertising,
} from "@/lib/acquisition/web-advertising";
import { captureIdentifiedClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

/**
 * The Ad Measurement setting. Measurement is ON by default and there is no
 * banner: this page (Control Center → Ad Measurement, or
 * /privacy/ad-measurement) is where anyone can turn it off, or back on.
 */
export function AdMeasurementControls() {
  const [on, setOn] = useState(true);
  const [busy, setBusy] = useState(false);
  const [needsUpdate, setNeedsUpdate] = useState(false);
  useEffect(() => {
    const update = () => {
      setOn(measurementOn(readClientAcquisition().consent));
      setNeedsUpdate(
        Capacitor.isNativePlatform() &&
          (!Capacitor.isPluginAvailable("HiiveAttribution") ||
            readClientAcquisition().meta_sdk_available === false),
      );
    };
    update();
    window.addEventListener(ACQUISITION_CHANGED, update);
    return () => window.removeEventListener(ACQUISITION_CHANGED, update);
  }, []);
  const change = async (enabled: boolean) => {
    setBusy(true);
    try {
      // Turning it back on is the one moment iOS may still need to ask.
      if (enabled) await requestNativeTracking();
    } catch {
      /* ATT is optional; limited measurement still applies. */
    } finally {
      setAdMeasurementConsent(enabled ? "granted" : "denied");
      setBusy(false);
    }
  };
  if (needsUpdate)
    return (
      <p className="text-sm text-text-secondary">
        Update Hiive from the App Store or Google Play to manage ad measurement.
      </p>
    );
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <label
          className="font-semibold text-text-primary"
          htmlFor="ad-measurement-switch"
        >
          Ad measurement
        </label>
        <Switch
          aria-label="Ad measurement"
          checked={on}
          disabled={busy}
          id="ad-measurement-switch"
          onCheckedChange={(checked) => void change(checked)}
        />
      </div>
      <p className="text-sm text-text-secondary">
        Hiive shares app installs, the events and pages you view, checkouts you
        start, signups, and purchases with Meta to measure and improve our ads.
        This includes your browser, IP address and basic device details, and
        hashed versions of your name, verified email address, phone number and
        its country, when available, to help Meta match these events.
      </p>
      <p className="text-sm text-text-secondary">
        Turning this off stops future sharing and does not affect your account,
        bookings, or purchases. On iPhone, Apple separately asks whether apps
        may track you; if you said no, only limited measurement without the
        advertising identifier is shared. Read our{" "}
        <a className="underline" href={LEGAL_LINKS.privacyPolicy}>
          Privacy Policy
        </a>
        .
      </p>
    </div>
  );
}
export function AcquisitionProvider() {
  const pathname = usePathname();
  const search = useSearchParams();
  const { authStatus, identity } = useAuthState();
  const userRef = useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname/search are the trigger — runs once per client navigation; the work reads window.location.
  useEffect(() => {
    observeAcquisitionUrl(window.location.href);
    void setupNativeAcquisition()
      .then(() => updateNativeAdvertising(userRef.current ?? undefined))
      .catch(() => {});
    updateWebAdvertising();
  }, [pathname, search]);
  useEffect(() => setupStoreClickTracking(), []);
  useEffect(() => setupAcquisitionStorageSync(), []);
  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | undefined;
    void setupNativeAcquisitionLifecycle()
      .then((remove) => {
        if (disposed) remove();
        else cleanup = remove;
      })
      .catch(() => {});
    return () => {
      disposed = true;
      cleanup?.();
    };
  }, []);
  useEffect(() => {
    if (authStatus === "unknown") return;
    const id = identity?.id ?? null;
    const owner = readClientAcquisition().owner_user_id;
    if ((owner && owner !== id) || (userRef.current && userRef.current !== id))
      resetAcquisitionAccountBoundary();
    userRef.current = id;
    if (id)
      saveClientAcquisition(
        { ...readClientAcquisition(), owner_user_id: id },
        false,
      );
    void syncAcquisition().then((synced) => {
      // The matching endpoint trusts the synced cookie's owner, so this waits.
      if (userRef.current === id)
        void setWebAdvertisingUser(synced ? id : null);
      if (synced && id && userRef.current === id)
        captureIdentifiedClientEvent(
          ANALYTICS_EVENTS.ACQUISITION_IDENTITY_LINKED,
          id,
          { viewer_user_id: id },
        );
    });
    void updateNativeAdvertising(id ?? undefined).catch(() => {});
  }, [authStatus, identity?.id]);
  useEffect(() => {
    const changed = () => {
      updateWebAdvertising();
      void updateNativeAdvertising(userRef.current ?? undefined).catch(
        () => {},
      );
    };
    window.addEventListener(ACQUISITION_CHANGED, changed);
    const poll = window.setInterval(() => {
      if (document.visibilityState === "visible")
        void updateNativeAdvertising(userRef.current ?? undefined).catch(
          () => {},
        );
    }, 30_000);
    return () => {
      window.clearInterval(poll);
      window.removeEventListener(ACQUISITION_CHANGED, changed);
    };
  }, []);
  return null;
}
